/**
 * Which difficulty set prices a route — and what a route is worth.
 *
 * One resolver for every consumer: the client (every points number the app
 * shows, through the ink-deriving `getDifficultyForDiscipline` wrapper), the
 * `updateGymStats` trigger (`gymStats.totalPoints`), the challenge board, the
 * points-map recompute and the recap / Wrapped e-mails. They used to be three
 * copies with one deliberate difference hidden in them; the difference is now
 * the `fallback` parameter, readable at the call site.
 */
import { DEFAULT_DISCIPLINES } from "./disciplines.js";
import { DEFAULT_DIFFICULTY } from "./difficultyLadder.js";

/**
 * Pick the difficulty config map for a route's discipline.
 *
 * A mixed gym (`disciplines.length > 1`) prices its lead routes off the
 * `settings.difficultyByDiscipline.lead` branch and its boulders off the
 * boulder branch; a missing branch falls through to the flat `customDifficulty`.
 * A single-discipline gym ignores a stray per-discipline map — the guard is what
 * keeps a boulder-only gym with a leftover `lead` branch from pricing off a
 * ladder nobody set up.
 *
 * `fallback` is what a gym with NO config resolves to, and it is the caller's
 * call because the answer differs by consumer:
 *   - `"none"` (default) → `null`. `gymStats` banks zero points for such a gym,
 *     and the client resolver returns the same `null` (every client caller
 *     spells its own `|| DIFFICULTY`).
 *   - `"builtin"` → `DEFAULT_DIFFICULTY`. The challenge board and the
 *     competition level use the built-in ladder, so they agree with the app's
 *     `|| DIFFICULTY` live preview on an uncustomised gym.
 * The fallback sits exactly where the `||` used to, so an EMPTY config is still
 * a config — a gym that deleted all its levels keeps its zeros, which is what
 * the client shows for it too.
 *
 * @param {object|null} gym  the gym doc (`settings`, `disciplines`)
 * @param {string} discipline  "lead" or anything else (→ boulder)
 * @param {{ fallback?: "none" | "builtin" }} [opts]
 * @returns {object|null} `{ [key]: { points, … } }`
 */
export function getDifficultyConfig(gym, discipline, { fallback = "none" } = {}) {
  const settings = (gym && gym.settings) || {};
  const disciplines = Array.isArray(gym && gym.disciplines) && gym.disciplines.length
    ? gym.disciplines
    : DEFAULT_DISCIPLINES;
  const disc = discipline === "lead" ? "lead" : "boulder";
  const missing = fallback === "builtin" ? DEFAULT_DIFFICULTY : null;
  if (settings.difficultyByDiscipline && settings.difficultyByDiscipline[disc] && disciplines.length > 1) {
    return settings.difficultyByDiscipline[disc] || missing;
  }
  return settings.customDifficulty || missing;
}

/**
 * Look up the point value for a (difficulty, discipline) pair from the gym's
 * settings. Falls back to 0 when missing — matches the client's "ungraded"
 * behaviour, which is also why an ungraded competition route is worth nothing.
 */
export function pointsForRoute(gym, routeDifficulty, routeDiscipline) {
  if (!routeDifficulty) return 0;
  const cfg = getDifficultyConfig(gym, routeDiscipline);
  const entry = cfg && cfg[routeDifficulty];
  return Number(entry && entry.points) || 0;
}

/**
 * Does this gym have a custom grade mapping configured — any level in
 * `settings.customDifficulty` that carries a `gradeRange`? One copy for the
 * platform admin's gym-health table and the MCP `platform_gym_health` tool.
 */
export function hasGradeMapping(gym) {
  const cd = gym && gym.settings && gym.settings.customDifficulty;
  if (!cd || typeof cd !== "object") return false;
  return Object.values(cd).some((d) => d && d.gradeRange);
}
